import { NgClass } from '@angular/common';
import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatListItem, MatListItemIcon, MatListItemTitle, MatListModule } from '@angular/material/list';
import { Router, RouterLink } from '@angular/router';
import { Utilities } from '@app/class/utilities';
import { CachedOsService } from '@app/component/page/company/os/services/cached-os.service';
import { Company, COMPANY_STATE } from '@app/interface/company';
import { SideMenu, SideMenuChild } from '@app/interface/side-menu';
import { MkgUser } from '@app/interface/user';
import { DataCacheService } from '@app/service/data-cache.service';
import { LayoutService } from '@app/service/layout.service';
import { ReverseRouterService } from '@app/service/reverse-router.service';
import { roles } from '@app/shared/lists/roles';
import { CanAccessPipe } from '@app/shared/pipes/can-access.pipe';
import { MenuSelectedPipe } from '@app/shared/pipes/menu-selected.pipe';
import { environment } from '@env/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MEKANIC_MENU, formatMenus } from '../header/menu/mekanic-menu';
import { TCAR_INTEGRATED_MENU } from '../header/menu/tcar-integrated-menu';
import { LegacyService } from '@app/component/page/company/legacy-os/legacy.service';
import { skipWhile, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { AccessProfile } from '@app/component/page/company/access-profile/models/access-profile';
import { DataService } from '@app/service/data.service';
import { RO_STATES } from '@app/shared/lists/ro-states';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'mkg-new-aside-menu',
  standalone: true,
  imports: [
    MatListModule,
    MatListItem,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslateModule,
    CanAccessPipe,
    RouterLink,
    MatListItemIcon,
    MatListItem,
    MatListItemTitle,
    MenuSelectedPipe
  ],
  templateUrl: './new-aside-menu.component.html',
  styleUrl: './new-aside-menu.component.scss',
})
export class NewAsideMenuComponent implements OnInit, OnDestroy {

  @ViewChild('searchInput', { static: false }) searchInput: ElementRef<HTMLInputElement>;

  private _destroy$ = new Subject<void>();

  private _commonOsService = inject(CachedOsService);
  private _dataCacheService = inject(DataCacheService);
  private _reverseRouterService = inject(ReverseRouterService);
  private _translateService = inject(TranslateService);
  private _legacyService = inject(LegacyService);
  private _dataService = inject(DataService);
  public layout = inject(LayoutService);
  public router = inject(Router);

  protected isProduction = environment.production;
  protected isTecincoMember: boolean;
  protected readonly roStatesKeys = Object.keys(RO_STATES);

  searchInputFormControl = new FormControl<string>("");

  menu: SideMenu[] = [];
  menusToDisplay: SideMenu[] = [];
  accessProfile: AccessProfile;
  userRole: number;

  ngOnInit(): void {
    this._dataService.ready$.pipe(
      takeUntil(this._destroy$),
      skipWhile(([user, company]) => !user || !company || company.currentState === COMPANY_STATE.blocked),
      tap(([user, company]) => {
        this._setMenu(company, user);
        this.isTecincoMember = user.isTecincoMember();
      }),
      switchMap(() => this.searchInputFormControl.valueChanges.pipe(startWith('')))
    ).subscribe(menuToSearchFor => {
      this.menusToDisplay = JSON.parse(JSON.stringify(this.menu));
      const menuToSearchForFormated = Utilities.removeDiacritics(menuToSearchFor?.trim() ?? "");
      if (menuToSearchForFormated) {
        this.layout.menu.expandAll = true;

        this.menusToDisplay = this.menusToDisplay.filter(topLevelMenu => {
          const topLevelMenuTranslated = Utilities.removeDiacritics(this._translateService.instant("MENU." + topLevelMenu.component));

          if (topLevelMenu.children?.length) {
            const topLevelMenuFiltred = topLevelMenu.children.slice().filter(childMenu => {
              const menuChildTranslated = Utilities.removeDiacritics(this._translateService.instant("MENU." + childMenu.component));
              if (childMenu.children?.length) {
                const availableChildMenus = childMenu.children.slice().filter(grandchildMenu => {
                  const grandchildMenuTranslated = Utilities.removeDiacritics(this._translateService.instant("MENU." + grandchildMenu.component));
                  return grandchildMenuTranslated.toLowerCase().includes(menuToSearchForFormated.toLowerCase());
                });

                if (!availableChildMenus.length && menuChildTranslated.toLowerCase().includes(menuToSearchForFormated.toLowerCase())) {
                  return true;
                } else if (availableChildMenus.length) {
                  childMenu.children = availableChildMenus;
                  return true
                } else {
                  return false;
                }
              } else {
                return menuChildTranslated.toLowerCase().includes(menuToSearchForFormated.toLowerCase());
              }
            })

            if (!topLevelMenuFiltred.length && topLevelMenuTranslated.toLowerCase().includes(menuToSearchForFormated.toLowerCase())) {
              return true;
            } else if (topLevelMenuFiltred.length) {
              topLevelMenu.children = topLevelMenuFiltred;
              return true;
            } else {
              return false;
            }
          } else {
            return topLevelMenuTranslated.toLowerCase().includes(menuToSearchForFormated.toLowerCase());
          }
        });
      } else {
        this.layout.collapseAll(false);
      }
    });

    this._dataService.accessProfile$.pipe(takeUntil(this._destroy$)).subscribe(profile => {
      this.accessProfile = profile;
    })

    this._dataService.userRole$.pipe(takeUntil(this._destroy$)).subscribe(role => {
      this.userRole = role;
    })
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _setMenu(company: Company, user: MkgUser) {
    const isTCarIntegrated = company.currentState === COMPANY_STATE.integrated;

    if (isTCarIntegrated) {
      this.menu = TCAR_INTEGRATED_MENU(company);
    } else {
      this.menu = MEKANIC_MENU(company, user);
    }

    if (!isTCarIntegrated) {
      this._legacyService.getAll().then(legacies => {
        if (Array.isArray(legacies) && legacies.length) {
          const legacyMenu = formatMenus([{
            component: 'legacy',
            isTopLevel: true,
            blockedRoles: [roles.mechanic.id],
            icon: 'join_left',
            href: ['company', company.id, 'legacy'],
            profileModule: "LEGACY",
          }])[0];
          this.menu.splice(-1, 0, legacyMenu); // insert at penultimate position
          this.searchInputFormControl.setValue(this.searchInputFormControl.value); // fire to update screen (filtered menus)
        }
      });
    }
  }

  public navigateOrExpand(menu: SideMenu | SideMenuChild) {
    if (!menu.href || this.layout.component === menu.component || menu.component === 'budgets') {
      this.layout.toggle(menu.component, menu["isTopLevel"]);
      return
    }
    this.router.navigate(menu.href, {
      queryParams: menu.queryParams
    });
  }

  public resetCache(component: string) {
    this._reverseRouterService.clear();
    this._dataCacheService.reset();

    if (!["budgets", "opened", "rejected", "budget", "closed-budget", "ended", "ro", "closed"].includes(component)) {
      this._commonOsService.clearCache();
    }
  }

  public focusSearchInput() {
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.nativeElement?.focus();
      }
    }, 100)
  }
}
